import {
  Box,
  BoxProps,
  IconButton,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { ComponentProps, useCallback, useEffect, useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import { formatPercent } from "@app/utils";
import {
  ChartActiveElement,
  ChartTooltip,
  RenderTooltip,
  useChartTooltip,
} from "@app/components/ChartTooltip";
import { addYears, format } from "date-fns";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { ChartDataset, ChartTypeRegistry } from "chart.js";
import { createPendoClassName, pendoClasses } from "@app/core/thirdParty/pendo";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { CarIconZoomIn, CarIconZoomOut } from "@app/components/Icons";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { CarAssetClassDftItem } from "@app/pages/assetClassAnalysis/useAssetClassSummaryStat";

type Options = ComponentProps<typeof Chart>["options"];

export interface DftTotalReturnsDataPoint {
  x: string;
  y: number;
}

interface DftTotalReturnsData {
  datasets: ChartDataset<"line", DftTotalReturnsDataPoint[]>[];
  xLabel: string;
  yLabel: string;
  historicLineDataLength: number;
}

export const getDftTotalReturnsData = ({
  theme,
  dftItem,
}: {
  theme: Theme;
  dftItem: CarAssetClassDftItem;
}): DftTotalReturnsData => {
  const result: DftTotalReturnsData = {
    datasets: [
      {
        type: "line",
        label: "Trend Line",
        id: "trend_line",
        data:
          dftItem.modelChartData.trend_x?.map((i, idx) => ({
            x: i ?? "",
            y: dftItem.modelChartData.trend_y_trend_line?.[idx] ?? 0,
          })) ?? [],
        borderColor: theme.palette.caravelOrangePrimary,
        pointBorderColor: theme.palette.caravelOrangePrimary,
        pointBackgroundColor: theme.palette.caravelOrangePrimary,
        pointBorderWidth: 0,
        pointRadius: 0,
        borderWidth: 1,
      },
      {
        type: "line",
        label: "Historic Line",
        id: "historic_line",
        data:
          dftItem.modelChartData.trend_x?.map((i, idx) => ({
            x: i ?? "",
            y: dftItem.modelChartData.trend_y_historic_line?.[idx] ?? 0,
          })) ?? [],
        borderColor: theme.palette.chartsColor.blue,
        pointBorderColor: theme.palette.chartsColor.blue,
        pointBackgroundColor: theme.palette.chartsColor.blue,
        pointBorderWidth: 0,
        pointRadius: 1,
        borderWidth: 1,
      },
    ],
    xLabel: `${dftItem.name} Total Returns`,
    yLabel: "Value Added Monthly Index",
    historicLineDataLength: dftItem.modelChartData.trend_x?.length ?? 0,
  };
  return result;
};

interface ChartDftTotalReturnsProps {
  sx?: BoxProps["sx"];
  dftItem: CarAssetClassDftItem;
  pendoPrefix: string;
  activeElement: ChartActiveElement;
  isWhiteContext: boolean;
}

export const ChartDftTotalReturns = ({
  sx,
  dftItem,
  pendoPrefix,
  activeElement,
  isWhiteContext,
}: ChartDftTotalReturnsProps) => {
  const theme = useTheme();

  const { datasets, historicLineDataLength, yLabel, xLabel } = useMemo(
    () =>
      getDftTotalReturnsData({
        theme,
        dftItem,
      }),
    [theme, dftItem],
  );

  const { tooltipPlugin, tooltipData } = useChartTooltip({
    chartName: "ChartTotalReturns",
    activeElement,
  });

  const chartRef = useRef<ChartJSOrUndefined<
    keyof ChartTypeRegistry,
    DftTotalReturnsDataPoint[],
    unknown
  > | null>();

  useEffect(() => {
    if (chartRef.current) {
      if (
        activeElement.element &&
        activeElement.element.chartName === "ChartDftVersusTenYearReturn" &&
        activeElement.element.datasetIndex === 3 &&
        activeElement.element.index < historicLineDataLength
      ) {
        chartRef.current.setActiveElements([
          { datasetIndex: 1, index: activeElement.element.index },
        ]);
        chartRef.current.tooltip?.setActiveElements(
          [{ datasetIndex: 1, index: activeElement.element.index }],
          { x: 0, y: 0 },
        );
      } else if (!activeElement.element) {
        chartRef.current.setActiveElements([]);
        chartRef.current.tooltip?.setActiveElements([], { x: 0, y: 0 });
      }
    }
  }, [activeElement, historicLineDataLength]);

  const renderTooltip: RenderTooltip = useCallback(
    (datasetIndex, dataIndex) => {
      const ds = datasets.at(datasetIndex);
      const value = ds?.data?.at(dataIndex)?.y ?? 0;
      const dateStr = ds?.data?.at(dataIndex)?.x;
      const date = dateStr ? format(new Date(dateStr), "yyyy MMM") : undefined;
      return (
        <>
          <Box
            sx={{
              fontSize: 15,
              fontWeight: 600,
              color: "black",
              whiteSpace: "nowrap",
            }}
          >
            <Typography variant="par01SemiBold" sx={{ fontSize: 15 }}>
              {ds?.label}
            </Typography>
            {Boolean(date) && (
              <>
                <br />
                <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
                  {date}
                </Typography>
              </>
            )}
            <br />
            <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
              {formatPercent(value, 1)}
            </Typography>
          </Box>
        </>
      );
    },
    [datasets],
  );

  const options = useMemo<Options>(
    () => ({
      maintainAspectRatio: false,
      animation: {
        duration: 500,
      },
      scales: {
        x: {
          // offset: true,
          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
          },
          grid: {
            display: false,
          },
          type: "time",
          time: {
            unit: "year",
          },
        },
        y: {
          offset: false,
          type: "logarithmic",
          title: {
            text: yLabel,
            display: true,
            font: {
              family: theme.typography.fontFamily,
              size: 16,
              weight: 600,
            },
          },

          ticks: {
            font: {
              family: theme.typography.fontFamily,
              size: 13,
              weight: 400,
            },
            color: theme.palette.softBlack,
            callback: (value) => "       ", // this will roughly align charts on left side  //(value === minY ? "" : `${value} x 10⁰`),
          },
          grid: {
            display: true,
            color: (ctx) => {
              return ctx.tick.value === 0
                ? theme.palette.gray7
                : theme.palette.gray2;
            },
            lineWidth: 1,
            tickColor: theme.palette.white,
          },
        },
      },
      interaction: {
        mode: "point",
        intersect: false,
      },
      plugins: {
        tooltip: tooltipPlugin,
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
          },
          limits: {
            x: {
              min: "original",
              max: "original",
              minRange: 5 * 365 * 24 * 60 * 60 * 1000, // max zoom level - 5 years
            },
          },
          zoom: {
            pinch: {
              enabled: true,
            },
            mode: "x",
          },
        },
        periods: {
          backgroundColor: "rgba(236, 238, 240, 0.5)",
          borderColor: "rgba(178, 179, 181, 0.5)",
          font: {
            family: theme.typography.fontFamily,
            style: "italic",
            size: 12,
          },
          textColor: theme.palette.black,
          labelPosition: "horizontal",
          items: [
            {
              start: addYears(new Date(), -10).getTime(),
              end: new Date().getTime(),
              label: "Timeframe too short for 10 year analysis",
            },
          ],
        },
        roundedBackground: ChartUtils.getRoundedBackgroundOptions({
          theme,
          isWhiteContext,
        }),
      },
    }),
    [theme, tooltipPlugin, yLabel, isWhiteContext],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", ...sx }}>
      <Box sx={{ display: "flex", zIndex: 1, alignItems: "flex-end" }}>
        <Typography
          variant="par03SemiBold"
          sx={{ fontSize: "19px", mb: 0.5, ml: 8 }}
        >
          {xLabel}
          <CarTooltipBox
            sx={{ display: "inline-block", ml: 0.5, mb: -0.5 }}
            className={pendoClasses.assetClassAnalysisDftAssetClassTotalReturns}
          />
        </Typography>
        <IconButton
          sx={{ ml: "auto" }}
          size="small"
          onClick={() => {
            chartRef.current?.zoom(1.5, "zoom");
          }}
        >
          <CarIconZoomIn fontSize="large" color="primary" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => {
            chartRef.current?.zoom(-1.5, "zoom");
          }}
        >
          <CarIconZoomOut fontSize="large" color="primary" />
        </IconButton>
      </Box>
      <ChartTooltip
        tooltipData={tooltipData}
        renderTooltip={renderTooltip}
        shiftY={0}
        sx={{
          mt: -1,
          position: "relative",
        }}
      >
        <CarTooltipBox
          sx={{ position: "absolute", left: 5, top: 72 }}
          className={pendoClasses.assetClassAnalysisDftValueAddedMonthlyIndex}
        />
        <Chart
          height={420}
          className={createPendoClassName(`${pendoPrefix}_chart`)}
          ref={chartRef}
          onDoubleClick={() => {
            chartRef.current?.resetZoom();
          }}
          type="line"
          datasetIdKey="id"
          data={{
            datasets,
          }}
          options={options}
          plugins={[ChartPlugins.roundedBackground, ChartPlugins.periods]}
        />
      </ChartTooltip>
    </Box>
  );
};
